import { canMirrorQuery } from '@kentik/ui-shared/query/mirror';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import { FormFieldState } from 'core/form/types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { getMeasurementItem } from '@kentik/ui-shared/query/measurements';
import { getDimensionDisplayLabel, getRollupKey } from '@kentik/ui-shared/query/query';
import Section from 'app/views/boards/SidebarSection';
import { Flex } from 'core/components';
import { Field, Select, Switch } from 'core/form';
import { observer } from 'mobx-react';
import { useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { TimeSeriesVizTypes } from 'app/views/ude/dataviews/util/highcharts';
import { usePanelEditContext } from 'app/views/boards/PanelEditContext';
import { FieldState } from './filters/filter.types';
import { useUdeForm } from './useUdeForm';

interface Props {
  panel: BoardPanelModel;
}

const AGGREGATE_VIZ_TYPES = ['bar', 'pie', 'sunburst'];
const MIRRORABLE_VIZ_TYPES = TimeSeriesVizTypes;

const YAxisMetricSection = ({ panel }: Props) => {
  const form = useUdeForm();
  const { queryModel } = panel;
  const onRunQuery = usePanelEditContext()?.onRunQuery;
  const measurementName = form.getValue('config.data.measurement');
  const metrics = form.getValue('config.data.metrics');
  const rollups = form.getValue('config.data.rollups');
  const chartType = form.getValue('config.chart.type');
  const selectedMetric = form.getValue('config.chart.yAxis.metric');
  const hasMultipleAxes = form.getValue('config.chart.yAxis.mirrorMode');
  const isAggregate = AGGREGATE_VIZ_TYPES.includes(chartType);
  const { measurement } = useDictionaryMeasurement(measurementName);

  const isMirrorable = useMemo(
    () =>
      MIRRORABLE_VIZ_TYPES.includes(chartType) &&
      !!queryModel?.query &&
      !!measurement &&
      canMirrorQuery(queryModel.query, measurement),
    [chartType, queryModel?.query, measurement]
  );

  const options = useMemo(() => {
    const opts: { label: string; value: string }[] = [];

    metrics.forEach((m) => {
      const item = measurement && getMeasurementItem(measurement, 'metric', m.name);
      const baseLabel = item?.Label || m.name;
      const metricLabel = item?.Window === 'count' ? getDimensionDisplayLabel(m.name, baseLabel) : baseLabel;

      if (isAggregate) {
        // Aggregate charts: show rollup-level options (avg, max, p95 each produce different values)
        const metricRollups = rollups.filter((r) => r.metric === m.name);
        if (metricRollups.length === 0) {
          opts.push({ label: metricLabel, value: m.name });
        } else {
          metricRollups.forEach((r) => {
            const key = getRollupKey(r);
            opts.push({ label: `${r.fn} ${metricLabel}`, value: key });
          });
        }
      } else {
        // Time series charts: show metric-level options (all rollups share the same line data)
        opts.push({ label: metricLabel, value: m.name });
      }
    });

    return opts;
  }, [metrics, measurement, isAggregate, rollups]);

  const handleChangeMirrorMode = useCallback(
    (_field: FormFieldState<boolean>, value: boolean) => {
      if (queryModel) {
        const hasData = queryModel.mainQueryRequest.hasRun && !queryModel.dirty;
        if (onRunQuery && value && hasData) {
          queryModel.forceRun = true;
          onRunQuery();
        }
      }
    },
    [onRunQuery, queryModel]
  );

  useEffect(() => {
    if (!form) {
      return;
    }

    const yAxisMetricField = form.getField('config.chart.yAxis.metric') as FieldState<string>;

    yAxisMetricField.setOptions(options);

    // selectedMetric hasn't been set
    if (!selectedMetric && options.length > 0) {
      yAxisMetricField.setValue(options[0].value);
    }

    // selectedMetric is no longer valid
    if (selectedMetric && !options.find((o) => o.value === selectedMetric)) {
      yAxisMetricField.setValue(options.length > 0 ? options[0].value : '');
    }

    yAxisMetricField.validateWithDebounce();
  }, [form, selectedMetric, options]);

  if (options.length <= 1 && !isMirrorable && !hasMultipleAxes) {
    return null;
  }

  return (
    <Section title="Y-Axis">
      <Flex flexDirection="column" px={2} gap={1} pt={1}>
        {options.length > 1 && (
          <Field name="config.chart.yAxis.metric" m={0} options={options}>
            <Select fill />
          </Field>
        )}
        {(isMirrorable || hasMultipleAxes) && (
          <Field name="config.chart.yAxis.mirrorMode" showLabel={false} m={0} onChange={handleChangeMirrorMode}>
            <Switch showFieldLabel />
          </Field>
        )}
        {hasMultipleAxes && (
          <Field name="config.chart.yAxis.syncAxes" showLabel={false} m={0}>
            <Switch showFieldLabel />
          </Field>
        )}
      </Flex>
    </Section>
  );
};

export default observer(YAxisMetricSection);
